import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  leadsApi,
  Lead,
  LeadStatus,
  LeadSubStatus,
  BulkImportResult,
} from '@/api/leads';
import { useAuth } from '@/contexts/AuthContext';
import { isCrmViewOnlyUser, shouldMaskLeadPhone } from '@/utils/crmAccess';
import { maskPhoneDisplay } from '@/utils/stringUtils';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import {
  LEAD_STATUS_OPTIONS,
  LEAD_SUBSTATUS_OPTIONS_BY_STATUS,
  leadStatusBadgeClass,
  formatLeadStatusLabel,
  formatLeadSubStatusLabel,
  formatLeadSubStatusLineWithDnp,
  formatPriorLeadStatusLine,
} from '@/lib/crmLeadStyles';
import {
  AESTHETIC_PROCEDURE_OPTIONS,
  BANT_TIER_OPTIONS,
  bantTierBadgeClass,
  formatLeadProcedureLabel,
  type BantTierFilter,
} from '@/lib/aestheticProcedure';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import { isExitedEmployeesBucketId } from '@/constants/crmExitedEmployees';
import { normalizePersonName, normalizePersonNameOptional } from '@/lib/personName';
import { isActiveCustomerCallbackAt } from '@/utils/lead-customer-callback.util';
import { leadCampaignLabel } from '@/lib/leadCampaignDisplay';
import { Button } from '@/components/ui/button';
import { FilterMultiSelect } from '@/components/ui/filter-multi-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LeadDetailModal } from '@/components/crm/LeadDetailModal';
import { LeadQuickCallButton } from '@/components/crm/LeadQuickCallButton';

const INBOUND_WA_POLL_MS = 3000;
const INBOUND_WA_LAST_SEEN_KEY = 'thms:lastInboundWhatsAppSeenAt';

type CrmBillingSourceRow = {
  source: string;
  opdBillToday: number;
  opdBillMtd: number;
  ipdToday: number;
  ipdMtd: number;
};

type CrmBillingMetricKey =
  | 'opdBillToday'
  | 'opdBillMtd'
  | 'ipdToday'
  | 'ipdMtd'
  | 'sarvamOpdBooked'
  | 'sarvamOpdVisited'
  | 'sarvamReminderConfirmed'
  | 'sarvamIpd';

type CrmBillingDrill = {
  metric: CrmBillingMetricKey;
  staffId?: string;
  repLabel: string;
};

const CRM_BILLING_METRIC_LABELS: Record<CrmBillingMetricKey, string> = {
  opdBillToday: 'OPD bill today',
  opdBillMtd: 'OPD bill MTD',
  ipdToday: 'IPD today',
  ipdMtd: 'IPD MTD',
  sarvamOpdBooked: 'Sarvam booked OPDs',
  sarvamOpdVisited: 'Sarvam visited OPDs',
  sarvamReminderConfirmed: 'Agent 2 reminder confirmed',
  sarvamIpd: 'Sarvam IPDs',
};

function formatLeadSourceLabel(source: string): string {
  return String(source || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function istDateRange(which: 'today' | 'week' | 'month'): { from: string; to: string } {
  const IST_OFFSET_MINUTES = 330;
  const now = new Date();
  const istNow = new Date(now.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
  const y = istNow.getUTCFullYear();
  const m = istNow.getUTCMonth();
  const d = istNow.getUTCDate();

  const startOfDayUtc = (yy: number, mm: number, dd: number) =>
    new Date(Date.UTC(yy, mm, dd, 0, 0, 0, 0) - IST_OFFSET_MINUTES * 60 * 1000);
  const endOfDayUtc = (yy: number, mm: number, dd: number) =>
    new Date(Date.UTC(yy, mm, dd, 23, 59, 59, 999) - IST_OFFSET_MINUTES * 60 * 1000);

  let from: Date;
  let to: Date;
  if (which === 'today') {
    from = startOfDayUtc(y, m, d);
    to = endOfDayUtc(y, m, d);
  } else if (which === 'week') {
    // last 7 days including today (IST)
    const start = new Date(Date.UTC(y, m, d, 0, 0, 0, 0));
    start.setUTCDate(start.getUTCDate() - 6);
    from = new Date(start.getTime() - IST_OFFSET_MINUTES * 60 * 1000);
    to = endOfDayUtc(y, m, d);
  } else {
    // month-to-date (IST)
    from = startOfDayUtc(y, m, 1);
    to = endOfDayUtc(y, m, d);
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

/** IST calendar date keys (YYYY-MM-DD) for manager team performance. */
function istMonthToDateKeys(): { from: string; to: string } {
  const IST_OFFSET_MINUTES = 330;
  const now = new Date();
  const istNow = new Date(now.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
  const y = istNow.getUTCFullYear();
  const m = istNow.getUTCMonth();
  const d = istNow.getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return { from: `${y}-${pad(m + 1)}-01`, to: `${y}-${pad(m + 1)}-${pad(d)}` };
}

const TEAM_PERF_MAX_DAYS = 62;

function istDateKeyToUtcMs(dateKey: string): number {
  const [y, mo, d] = dateKey.split('-').map((x) => parseInt(x, 10));
  const IST_OFFSET_MINUTES = 330;
  return Date.UTC(y, mo - 1, d, 0, 0, 0, 0) - IST_OFFSET_MINUTES * 60 * 1000;
}

function clampManagerTeamPerfRange(from: string, to: string): { from: string; to: string } {
  if (!from || !to) return istMonthToDateKeys();
  let fromMs = istDateKeyToUtcMs(from);
  let toMs = istDateKeyToUtcMs(to);
  if (fromMs > toMs) {
    const swap = from;
    from = to;
    to = swap;
    fromMs = istDateKeyToUtcMs(from);
    toMs = istDateKeyToUtcMs(to);
  }
  const maxMs = TEAM_PERF_MAX_DAYS * 24 * 60 * 60 * 1000;
  if (toMs - fromMs > maxMs) {
    fromMs = toMs - maxMs;
    const ist = new Date(fromMs + 330 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    from = `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`;
  }
  return { from, to };
}

type ManagerTeamPerfMetricKey =
  | 'open_leads'
  | 'contacted'
  | 'interested'
  | 'follow_up'
  | 'opd_visited'
  | 'follow_ups_missed'
  | 'dnp_missed'
  | 'ai_revived_dnp'
  | 'ai_booked_opd'
  | 'ai_marked_lost';

type ManagerTeamPerfDrill = {
  metric: ManagerTeamPerfMetricKey;
  repId: string;
  repLabel: string;
};

const MANAGER_TEAM_PERF_ALL_REPS_ID = '__all__';

const MANAGER_TEAM_PERF_METRIC_LABELS: Record<ManagerTeamPerfMetricKey, string> = {
  open_leads: 'Open leads',
  contacted: 'Contacted',
  interested: 'Interested',
  follow_up: 'Follow up',
  opd_visited: 'OPD visited',
  follow_ups_missed: 'Follow-ups missed',
  dnp_missed: 'DNP missed',
  ai_revived_dnp: 'AI revived DNP',
  ai_booked_opd: 'AI booked OPD',
  ai_marked_lost: 'AI marked lost',
};

const CONTACT_RELATIONSHIP_OPTIONS = [
  'Husband',
  'Wife',
  'Son',
  'Daughter',
  'Brother',
  'Sister',
  'Father',
  'Mother',
  'Friend',
  'Partner',
  'Relative',
  'Self',
  'Other',
] as const;

export default function LeadsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const agencyViewOnly = isCrmViewOnlyUser(user);
  const queryClient = useQueryClient();
  const inboundPollRef = useRef<number | null>(null);
  const canManageTeamLeads = user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'SALES_MANAGER';
  /** Sales staff only — admins/super admins manage the team and should not get the auto popup. */
  const autoShowTodayFollowUpPopup =
    user?.role === 'SALES_PERSON' || user?.role === 'SALES_MANAGER';
  const showManagerDashboard = user?.role === 'SALES_MANAGER' || user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';
  const showBillingAttributionDashboard =
    !!user?.hospitalId &&
    !agencyViewOnly &&
    (showManagerDashboard || user?.role === 'SALES_PERSON');
  const [statusFilters, setStatusFilters] = useState<LeadStatus[]>([]);
  const [sarvamCalledFilter, setSarvamCalledFilter] = useState(false);
  const [subStatusFilters, setSubStatusFilters] = useState<Array<LeadSubStatus | '__none__'>>([]);
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [campaignFilters, setCampaignFilters] = useState<string[]>([]);
  const [agencyFilter, setAgencyFilter] = useState<string>('');
  const [surgeryFilter, setSurgeryFilter] = useState<'all' | 'true' | 'false' | 'unset'>('all');
  const [bantTierFilter, setBantTierFilter] = useState<BantTierFilter>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('__all__');
  const [search, setSearch] = useState('');
  const [createdFrom, setCreatedFrom] = useState<string>('');
  const [createdTo, setCreatedTo] = useState<string>('');
  const [createdFromTime, setCreatedFromTime] = useState<string>('');
  const [createdToTime, setCreatedToTime] = useState<string>('');
  const [followUpFrom, setFollowUpFrom] = useState<string>('');
  const [followUpTo, setFollowUpTo] = useState<string>('');
  const [followUpFromTime, setFollowUpFromTime] = useState<string>('');
  const [followUpToTime, setFollowUpToTime] = useState<string>('');
  const [sortBy, setSortBy] = useState<
    | 'createdAt'
    | 'updatedAt'
    | 'patientName'
    | 'phone'
    | 'source'
    | 'campaignName'
    | 'status'
    | 'assignedToId'
    | 'nextFollowUpAt'
    | 'specialisation'
    | 'isSurgeryCase'
    | 'ingestedViaApiKeyId'
  >('updatedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  type NewLeadForm = {
    patientName: string;
    phone: string;
    alternatePhone: string;
    email: string;
    ageLocal: string;
    gender: 'unset' | 'MALE' | 'FEMALE' | 'OTHER';
    source: string;
    sourceDetails: string;
    campaignName: string;
    notes: string;
    contactPersonName: string;
    contactRelationship: string;
    isSurgeryCase: 'unset' | 'yes' | 'no';
    aestheticProcedure: string;
    tentativeSurgeryDateLocal: string;
  };

  const [newLead, setNewLead] = useState<NewLeadForm>({
    patientName: '',
    phone: '',
    alternatePhone: '',
    email: '',
    ageLocal: '',
    gender: 'unset',
    source: '',
    sourceDetails: '',
    campaignName: '',
    notes: '',
    contactPersonName: '',
    contactRelationship: '',
    isSurgeryCase: 'unset',
    aestheticProcedure: '',
    tentativeSurgeryDateLocal: '',
  });

  const createFormAestheticOptions = useMemo(() => {
    const v = newLead.aestheticProcedure.trim();
    const base = [...AESTHETIC_PROCEDURE_OPTIONS] as string[];
    if (v && !base.includes(v)) base.push(v);
    return base;
  }, [newLead.aestheticProcedure]);

  const createFormRelationshipOptions = useMemo(() => {
    const v = newLead.contactRelationship.trim();
    const base = [...CONTACT_RELATIONSHIP_OPTIONS] as string[];
    if (v && !base.includes(v)) base.push(v);
    return base;
  }, [newLead.contactRelationship]);

  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkHospitalId, setBulkHospitalId] = useState('');
  const [bulkResult, setBulkResult] = useState<BulkImportResult | null>(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);

  const [selectedLeadIds, setSelectedLeadIds] = useState<Record<string, boolean>>({});
  const [bulkAssignToId, setBulkAssignToId] = useState<string>('');

  const selectedIds = useMemo(() => Object.keys(selectedLeadIds).filter((id) => selectedLeadIds[id]), [selectedLeadIds]);

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['crm-staff-options', user?.hospitalId],
    queryFn: async () => {
      const rows = await hospitalAdminApi.getStaff();
      return (rows || []).filter((s: any) => s?.role === 'SALES_PERSON' && (s?.status ?? 'ACTIVE') === 'ACTIVE');
    },
    enabled: !!user?.hospitalId && canManageTeamLeads,
    staleTime: 60_000,
  });

  const [alertAssignDraft, setAlertAssignDraft] = useState<Record<string, string>>({});
  const [missedSelectedIds, setMissedSelectedIds] = useState<Record<string, boolean>>({});
  const [noActSelectedIds, setNoActSelectedIds] = useState<Record<string, boolean>>({});
  const [mgrTeamRange, setMgrTeamRange] = useState(istMonthToDateKeys);
  const [teamPerfDrill, setTeamPerfDrill] = useState<ManagerTeamPerfDrill | null>(null);
  const [teamPerfDrillLead, setTeamPerfDrillLead] = useState<Lead | null>(null);
  const [missedBulkAssignToId, setMissedBulkAssignToId] = useState<string>('');
  const [noActBulkAssignToId, setNoActBulkAssignToId] = useState<string>('');
  const [partnerDumpOpen, setPartnerDumpOpen] = useState(false);
  const [partnerDumpFrom, setPartnerDumpFrom] = useState('');
  const [partnerDumpTo, setPartnerDumpTo] = useState('');
  const [partnerDumpDownloading, setPartnerDumpDownloading] = useState(false);

  const missedSelected = useMemo(
    () => Object.keys(missedSelectedIds).filter((id) => missedSelectedIds[id]),
    [missedSelectedIds]
  );
  const noActSelected = useMemo(
    () => Object.keys(noActSelectedIds).filter((id) => noActSelectedIds[id]),
    [noActSelectedIds]
  );

  type PendingAssign =
    | { mode: 'single'; leadId: string; assignedToId: string | null }
    | { mode: 'bulk'; leadIds: string[]; assignedToId: string | null };

  const [pendingAssign, setPendingAssign] = useState<PendingAssign | null>(null);
  const [assignmentReasonInput, setAssignmentReasonInput] = useState('');

  const assignLeadMut = useMutation({
    mutationFn: async (payload: {
      leadId: string;
      assignedToId: string | null;
      assignmentReason: string;
    }) => leadsApi.assignLead(payload.leadId, payload.assignedToId, payload.assignmentReason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['mgr-alerts'] });
      toast.success('Lead reassigned');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Reassign failed');
    },
  });

  const bulkAssignMut = useMutation({
    mutationFn: async (payload: {
      leadIds: string[];
      assignedToId: string | null;
      assignmentReason: string;
    }) => leadsApi.bulkAssign(payload.leadIds, payload.assignedToId, payload.assignmentReason),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['mgr-alerts'] });
      toast.success(`Assigned ${res.data?.data?.updated ?? 0} lead(s)`);
      setSelectedLeadIds({});
      setBulkAssignToId('');
      setMissedSelectedIds({});
      setNoActSelectedIds({});
      setMissedBulkAssignToId('');
      setNoActBulkAssignToId('');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Bulk assign failed');
    },
  });

  const submitPendingAssign = () => {
    const reason = assignmentReasonInput.trim();
    if (reason.length < 3) {
      toast.error('Enter a reason for assignment (at least 3 characters)');
      return;
    }
    if (!pendingAssign) return;
    if (pendingAssign.mode === 'single') {
      const leadId = pendingAssign.leadId;
      assignLeadMut.mutate(
        {
          leadId,
          assignedToId: pendingAssign.assignedToId,
          assignmentReason: reason,
        },
        {
          onSuccess: () => {
            setPendingAssign(null);
            setAssignmentReasonInput('');
            setAlertAssignDraft((d) => ({ ...d, [leadId]: '__keep__' }));
          },
        }
      );
    } else {
      bulkAssignMut.mutate(
        {
          leadIds: pendingAssign.leadIds,
          assignedToId: pendingAssign.assignedToId,
          assignmentReason: reason,
        },
        {
          onSuccess: () => {
            setPendingAssign(null);
            setAssignmentReasonInput('');
          },
        }
      );
    }
  };

  useEffect(() => {
    setPage(1);
  }, [
    statusFilters,
    subStatusFilters,
    sourceFilters,
    campaignFilters,
    agencyFilter,
    surgeryFilter,
    bantTierFilter,
    ownerFilter,
    search,
    sortBy,
    sortDir,
  ]);

  const toggleSort = (
    by:
      | 'createdAt'
      | 'updatedAt'
      | 'patientName'
      | 'phone'
      | 'source'
      | 'campaignName'
      | 'status'
      | 'assignedToId'
      | 'nextFollowUpAt'
      | 'specialisation'
      | 'isSurgeryCase'
      | 'ingestedViaApiKeyId'
  ) => {
    setSortBy((prev) => {
      if (prev !== by) {
        const dateCols = new Set(['createdAt', 'updatedAt', 'nextFollowUpAt']);
        setSortDir(dateCols.has(by) ? 'desc' : 'asc');
        return by;
      }
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return prev;
    });
  };

  const sortIndicator = (by: string) => {
    if (sortBy !== by) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const { data: leadFilterOptions } = useQuery({
    queryKey: ['lead-filter-options', user?.hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(),
    enabled: !!user?.hospitalId,
  });

  const sourceFilterOptions = useMemo(
    () =>
      (leadFilterOptions?.sources?.length
        ? leadFilterOptions.sources
        : [
            'GOOGLE_PAID',
            'GMB',
            'META',
            'PRACTO',
            'HEXA',
            'REFERRAL',
            'WALK_IN',
            'OTHER',
            'EXOTEL',
            'ORGANIC_DIRECT',
            'ORGANIC_GOOGLE',
            'DIRECT_LANDING',
            'INSTAGRAM_ORGANIC',
          ]
      ).map((s) => ({ value: s, label: s })),
    [leadFilterOptions?.sources]
  );

  const campaignFilterOptions = useMemo(
    () => [
      { value: '__none__', label: 'No campaign' },
      ...(leadFilterOptions?.campaignNames ?? []).map((c) => ({ value: c, label: c })),
    ],
    [leadFilterOptions?.campaignNames]
  );

  const subStatusFilterOptions = useMemo(() => {
    const statuses = statusFilters.length ? statusFilters : LEAD_STATUS_OPTIONS;
    const seen = new Set<LeadSubStatus>();
    for (const st of statuses) {
      for (const ss of LEAD_SUBSTATUS_OPTIONS_BY_STATUS[st] ?? []) {
        seen.add(ss);
      }
    }
    return [
      { value: '__none__', label: 'Not specified' },
      ...Array.from(seen).map((ss) => ({
        value: ss,
        label: formatLeadSubStatusLabel(ss),
      })),
    ];
  }, [statusFilters]);

  useEffect(() => {
    setSubStatusFilters((prev) => {
      const allowed = new Set(subStatusFilterOptions.map((o) => o.value));
      const next = prev.filter((v) => allowed.has(v));
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) return prev;
      return next;
    });
  }, [subStatusFilterOptions]);

  const todayRange = useMemo(() => istDateRange('today'), []);
  const weekRange = useMemo(() => istDateRange('week'), []);
  const monthRange = useMemo(() => istDateRange('month'), []);

  const { data: mgrToday } = useQuery({
    queryKey: ['mgr-overview', 'today', user?.hospitalId],
    queryFn: async () => (await leadsApi.getManagerOverview(todayRange)).data.data,
    enabled: !!user?.hospitalId && showManagerDashboard,
    staleTime: 60_000,
  });
  const { data: mgrWeek } = useQuery({
    queryKey: ['mgr-overview', 'week', user?.hospitalId],
    queryFn: async () => (await leadsApi.getManagerOverview(weekRange)).data.data,
    enabled: !!user?.hospitalId && showManagerDashboard,
    staleTime: 60_000,
  });
  const { data: mgrMonth } = useQuery({
    queryKey: ['mgr-overview', 'month', user?.hospitalId],
    queryFn: async () => (await leadsApi.getManagerOverview(monthRange)).data.data,
    enabled: !!user?.hospitalId && showManagerDashboard,
    staleTime: 60_000,
  });
  const {
    data: mgrRep,
    isLoading: mgrRepLoading,
    isError: mgrRepError,
  } = useQuery({
    queryKey: ['mgr-rep-metrics', mgrTeamRange, user?.hospitalId],
    queryFn: async () =>
      (await leadsApi.getManagerRepMetrics({ from: mgrTeamRange.from, to: mgrTeamRange.to })).data
        .data,
    enabled: !!user?.hospitalId && showManagerDashboard,
    staleTime: 60_000,
  });

  const teamPerfRange = useMemo(() => {
    const fromKey = mgrRep?.period?.fromKey ?? mgrTeamRange.from;
    const toKey = mgrRep?.period?.toKey ?? mgrTeamRange.to;
    return { from: fromKey, to: toKey };
  }, [mgrRep?.period?.fromKey, mgrRep?.period?.toKey, mgrTeamRange.from, mgrTeamRange.to]);

  const { data: teamPerfDrillLeads = [], isLoading: teamPerfDrillLoading } = useQuery({
    queryKey: [
      'mgr-rep-metric-leads',
      teamPerfDrill?.metric,
      teamPerfDrill?.repId,
      teamPerfRange,
      user?.hospitalId,
    ],
    queryFn: async () =>
      (
        await leadsApi.getManagerRepMetricLeads({
          metric: teamPerfDrill!.metric,
          repId: teamPerfDrill!.repId,
          from: teamPerfRange.from,
          to: teamPerfRange.to,
        })
      ).data.data.items,
    enabled: !!teamPerfDrill && !!user?.hospitalId,
    staleTime: 30_000,
  });
  const { data: mgrAlerts } = useQuery({
    queryKey: ['mgr-alerts', user?.hospitalId],
    queryFn: async () => (await leadsApi.getManagerAlerts()).data.data,
    enabled: !!user?.hospitalId && showManagerDashboard,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: crmBilling } = useQuery({
    queryKey: ['crm-billing-attribution', user?.hospitalId],
    queryFn: async () => (await leadsApi.getCrmBillingAttribution()).data.data,
    enabled: !!user?.hospitalId && showBillingAttributionDashboard,
    staleTime: 60_000,
  });

  const [billingDrill, setBillingDrill] = useState<CrmBillingDrill | null>(null);
  const [billingDrillLead, setBillingDrillLead] = useState<Lead | null>(null);

  const { data: billingDrillLeads = [], isLoading: billingDrillLoading } = useQuery({
    queryKey: ['crm-billing-metric-leads', billingDrill?.metric, billingDrill?.staffId, user?.hospitalId],
    queryFn: async () =>
      (
        await leadsApi.getCrmBillingMetricLeads({
          metric: billingDrill!.metric,
          staffId: billingDrill!.staffId,
        })
      ).data.data.items,
    enabled: !!billingDrill && !!user?.hospitalId,
    staleTime: 30_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      'leads',
      statusFilters,
      sarvamCalledFilter,
      subStatusFilters,
      sourceFilters,
      campaignFilters,
      agencyFilter,
      surgeryFilter,
      bantTierFilter,
      ownerFilter,
      search,
      createdFrom,
      createdFromTime,
      createdTo,
      createdToTime,
      followUpFrom,
      followUpFromTime,
      followUpTo,
      followUpToTime,
      page,
      user?.role,
      sortBy,
      sortDir,
    ],
    queryFn: async () => {
      // All reporting in this app is IST-based. Convert the picked calendar date
      // (YYYY-MM-DD) into UTC instants that represent IST day boundaries.
      const IST_OFFSET_MINUTES = 330;
      const istDateTimeIso = (
        date: string,
        time: string,
        which: 'start' | 'end'
      ): string | undefined => {
        const d = String(date || '').trim();
        if (!d) return undefined;
        const [yy, mm, dd] = d.split('-').map((n) => parseInt(n, 10));
        if (!yy || !mm || !dd) return undefined;

        const t = String(time || '').trim();
        const [hhRaw, minRaw] = t ? t.split(':') : [];
        const hh = t ? parseInt(String(hhRaw || '0'), 10) : which === 'start' ? 0 : 23;
        const mi = t ? parseInt(String(minRaw || '0'), 10) : which === 'start' ? 0 : 59;
        const ss = which === 'start' ? 0 : 59;
        const ms = which === 'start' ? 0 : 999;

        if (
          !Number.isFinite(hh) ||
          !Number.isFinite(mi) ||
          hh < 0 ||
          hh > 23 ||
          mi < 0 ||
          mi > 59
        ) {
          return undefined;
        }

        const localUtcMs = Date.UTC(yy, mm - 1, dd, hh, mi, ss, ms);
        const utcMs = localUtcMs - IST_OFFSET_MINUTES * 60 * 1000;
        return new Date(utcMs).toISOString();
      };
      const istBoundaryIso = (
        d: string,
        which: 'start' | 'end'
      ): string | undefined => {
        const s = String(d || '').trim();
        if (!s) return undefined;
        const [yy, mm, dd] = s.split('-').map((n) => parseInt(n, 10));
        if (!yy || !mm || !dd) return undefined;
        const localUtcMs =
          which === 'start'
            ? Date.UTC(yy, mm - 1, dd, 0, 0, 0, 0)
            : Date.UTC(yy, mm - 1, dd, 23, 59, 59, 999);
        const utcMs = localUtcMs - IST_OFFSET_MINUTES * 60 * 1000;
        return new Date(utcMs).toISOString();
      };
      const res = await leadsApi.listLeads({
        status: statusFilters.length ? statusFilters.join(',') : undefined,
        sarvamCalled: sarvamCalledFilter ? 'true' : undefined,
        leadSubStatus: subStatusFilters.length ? subStatusFilters.join(',') : undefined,
        sortBy,
        sortDir,
        source: sourceFilters.length ? sourceFilters.join(',') : undefined,
        campaign: campaignFilters.length ? campaignFilters.join(',') : undefined,
        ingestedViaApiKeyId: agencyFilter || undefined,
        isSurgeryCase: surgeryFilter === 'all' ? undefined : surgeryFilter,
        bantTier: bantTierFilter === 'all' ? undefined : bantTierFilter,
        assignedToId: ownerFilter === '__all__' ? undefined : ownerFilter,
        search: search || undefined,
        from: createdFromTime
          ? istDateTimeIso(createdFrom, createdFromTime, 'start')
          : istBoundaryIso(createdFrom, 'start'),
        to: createdToTime
          ? istDateTimeIso(createdTo, createdToTime, 'end')
          : istBoundaryIso(createdTo, 'end'),
        followUpFrom: followUpFromTime
          ? istDateTimeIso(followUpFrom, followUpFromTime, 'start')
          : istBoundaryIso(followUpFrom, 'start'),
        followUpTo: followUpToTime
          ? istDateTimeIso(followUpTo, followUpToTime, 'end')
          : istBoundaryIso(followUpTo, 'end'),
        page,
        pageSize,
      });
      return res.data.data;
    },
    refetchInterval: 60_000,
  });

  const leads = data?.items ?? [];
  const totalLeads = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalLeads / pageSize));

  const dismissFollowMut = useMutation({
    mutationFn: (leadId: string) => leadsApi.dismissFollowUpToday(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['today-followups'] });
      queryClient.invalidateQueries({ queryKey: ['lead-followups-upcoming'] });
    },
  });

  const snoozeFollowMut = useMutation({
    mutationFn: ({ leadId, hours }: { leadId: string; hours?: number }) =>
      leadsApi.snoozeFollowUp(leadId, hours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['today-followups'] });
      queryClient.invalidateQueries({ queryKey: ['lead-followups-upcoming'] });
    },
  });

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [followupPopupOpen, setFollowupPopupOpen] = useState(false);
  const todayFollowUpPopupDismissedRef = useRef(false);

  const openLeadDetail = (lead: Lead) => {
    setSelectedLead(lead);
    setDetailModalOpen(true);
  };

  const { data: todayFollowUps } = useQuery({
    queryKey: ['today-followups', user?.id],
    queryFn: async () => {
      const res = await leadsApi.getTodayFollowUps();
      return res.data.data;
    },
    enabled: !!user && autoShowTodayFollowUpPopup,
  });

  useEffect(() => {
    if (!autoShowTodayFollowUpPopup) return;
    if (todayFollowUpPopupDismissedRef.current) return;
    if (todayFollowUps && todayFollowUps.length > 0) {
      setFollowupPopupOpen(true);
    }
  }, [todayFollowUps, autoShowTodayFollowUpPopup]);

  // Inbound WhatsApp popups (customer -> hospital). Polling-based so it works without WebSockets.
  useEffect(() => {
    if (!user?.id || !user?.hospitalId) return;
    if (user.role !== 'SALES_PERSON') return;

    const tick = async () => {
      try {
        const lastSeen = localStorage.getItem(INBOUND_WA_LAST_SEEN_KEY) || '';
        const res = await leadsApi.listInboundWhatsApp(lastSeen ? { since: lastSeen } : undefined);
        const items = (res.data?.data as any)?.items ?? [];
        if (!Array.isArray(items) || items.length === 0) return;

        const newest = items[items.length - 1];
        if (newest?.createdAt) localStorage.setItem(INBOUND_WA_LAST_SEEN_KEY, String(newest.createdAt));

        // Refresh list so lead moves to top by recency.
        queryClient.invalidateQueries({ queryKey: ['leads'] });

        for (const m of items) {
          const lead = m?.lead;
          const body = String(m?.body || '').trim();
          const name = String(lead?.patientName || 'Customer');
          toast(`WhatsApp from ${name}`, {
            description: body.length > 180 ? `${body.slice(0, 180)}…` : body || 'New inbound message',
            action: {
              label: 'Open lead',
              onClick: () => {
                const leadId = String(m?.leadId || '').trim();
                if (!leadId) return;
                const listRow = leads.find((l) => l.id === leadId);
                if (listRow) void openLeadDetail(listRow);
              },
            },
          } as any);
        }
    } catch {
        // ignore polling errors
      }
    };

    void tick();
    inboundPollRef.current = window.setInterval(() => void tick(), INBOUND_WA_POLL_MS);
    return () => {
      if (inboundPollRef.current) window.clearInterval(inboundPollRef.current);
      inboundPollRef.current = null;
    };
  }, [user?.id, user?.hospitalId, user?.role]);

  // Deep-link from notification bell / new-lead popup: ?openLeadId=<id>
  // Fetch by id when the lead is missing from the filtered list (filters / other pages).
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const openLeadId = params.get('openLeadId');
    if (!openLeadId) return;

    let cancelled = false;

    const clearParam = () => {
      params.delete('openLeadId');
      const next = params.toString();
      navigate({ pathname: location.pathname, search: next ? `?${next}` : '' }, { replace: true });
    };

    const fromList = leads.find((l) => l.id === openLeadId);
    if (fromList) {
      void openLeadDetail(fromList);
      clearParam();
      return;
    }

    void (async () => {
      try {
        const res = await leadsApi.getLeadById(openLeadId);
        if (cancelled) return;
        const detail = res.data?.data;
        if (detail?.id) {
          openLeadDetail(detail as Lead);
          clearParam();
        }
      } catch {
        // Lead not found / no access — leave param so a later list load can still try, or drop after clear
        if (!cancelled) clearParam();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, leads]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Leads</h1>
        <div className="flex items-center gap-2">
          {(user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'SALES_MANAGER') && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                const res = await leadsApi.downloadLeadDump();
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `leads-dump-${new Date().toISOString().slice(0, 10)}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
              }}
            >
              Download Lead Dump
            </Button>
          )}
          {user?.role === 'EXTERNAL_PARTNER' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPartnerDumpFrom(createdFrom || '');
                setPartnerDumpTo(createdTo || '');
                setPartnerDumpOpen(true);
              }}
            >
              Download Lead Dump
            </Button>
          )}
          {!agencyViewOnly ? (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setBulkOpen(true);
                  setBulkResult(null);
                  setBulkFile(null);
                  if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
                }}
              >
                Bulk upload
              </Button>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                + New lead
              </Button>
            </>
          ) : (
            <div className="text-xs text-muted-foreground">
              {user?.role === 'EXTERNAL_PARTNER' ? 'Read-only partner view' : 'Read-only agency view'}
            </div>
          )}
        </div>
      </div>

      {showBillingAttributionDashboard && (() => {
        const sourceRows: CrmBillingSourceRow[] =
          user?.role === 'SALES_PERSON'
            ? (crmBilling?.mineSources ?? [])
            : (crmBilling?.sources ?? []);
        const opdBillTodayTotal =
          user?.role === 'SALES_PERSON'
            ? (crmBilling?.mine?.opdBillToday ?? 0)
            : (crmBilling?.hospital?.opdBillToday ?? 0);
        const opdBillMtdTotal =
          user?.role === 'SALES_PERSON'
            ? (crmBilling?.mine?.opdBillMtd ?? 0)
            : (crmBilling?.hospital?.opdBillMtd ?? 0);
        const ipdTodayTotal =
          user?.role === 'SALES_PERSON'
            ? (crmBilling?.mine?.ipdToday ?? 0)
            : (crmBilling?.hospital?.ipdToday ?? 0);
        const ipdMtdTotal =
          user?.role === 'SALES_PERSON'
            ? (crmBilling?.mine?.ipdMtd ?? 0)
            : (crmBilling?.hospital?.ipdMtd ?? 0);
        const teamTotals = crmBilling?.hospital ?? {};
        const billingCounts = (row: { counts?: Record<string, number> }) => row.counts ?? {};

        const openBillingDrill = (
          metric: CrmBillingMetricKey,
          value: number,
          repLabel: string,
          staffId?: string,
        ) => {
          if (value <= 0) return;
          setBillingDrill({ metric, staffId, repLabel });
        };

        const BillingCountCell = ({
          value,
          metric,
          repLabel,
          staffId,
          emphasize,
        }: {
          value: number;
          metric: CrmBillingMetricKey;
          repLabel: string;
          staffId?: string;
          emphasize?: boolean;
        }) => {
          if (value <= 0) {
            return <span className="tabular-nums text-slate-400">0</span>;
          }
          return (
            <button
              type="button"
              className={`tabular-nums hover:underline ${
                emphasize
                  ? 'font-semibold text-indigo-800'
                  : 'font-medium text-indigo-700'
              }`}
              title="View leads"
              onClick={() => openBillingDrill(metric, value, repLabel, staffId)}
            >
              {value}
            </button>
          );
        };

        return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-indigo-600 to-blue-800 p-6 text-white shadow-lg ring-1 ring-indigo-500/30">
              <div className="text-lg font-semibold tracking-tight">OPD from CRM leads</div>
              <p className="mt-1 text-sm text-indigo-100/90">
                By lead source · one count per customer (IST). OPD consultation bill generated (bill date). Exotel excluded.
              </p>
              <div className="mt-3 flex gap-6 text-sm">
                <div>
                  <span className="text-indigo-200 text-xs uppercase">Total today</span>
                  <div className="text-2xl font-bold tabular-nums">{opdBillTodayTotal}</div>
                </div>
                <div>
                  <span className="text-indigo-200 text-xs uppercase">Total MTD</span>
                  <div className="text-2xl font-bold tabular-nums">{opdBillMtdTotal}</div>
                </div>
              </div>
              <div className="mt-4 max-h-52 overflow-y-auto rounded-lg bg-white/10 ring-1 ring-white/15">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-indigo-900/90 text-indigo-100">
                    <tr>
                      <th className="py-2 px-3 text-left font-medium">Source</th>
                      <th className="py-2 px-2 text-right font-medium">Today</th>
                      <th className="py-2 px-3 text-right font-medium">MTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceRows.map((row) => (
                      <tr key={row.source} className="border-t border-white/10">
                        <td className="py-1.5 px-3 font-medium">{formatLeadSourceLabel(row.source)}</td>
                        <td className="py-1.5 px-2 text-right tabular-nums">{row.opdBillToday}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums font-semibold">{row.opdBillMtd}</td>
                      </tr>
                    ))}
                    {sourceRows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 px-3 text-center text-indigo-200/80">
                          No OPD bills from CRM leads in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-800 p-6 text-white shadow-lg ring-1 ring-emerald-500/30">
              <div className="text-lg font-semibold tracking-tight">IPD from CRM leads</div>
              <p className="mt-1 text-sm text-emerald-100/90">
                By lead source · IPD discharges from CRM leads (discharge date, IST). Exotel excluded.
              </p>
              <div className="mt-3 flex gap-6 text-sm">
                <div>
                  <span className="text-emerald-200 text-xs uppercase">Total today</span>
                  <div className="text-2xl font-bold tabular-nums">{ipdTodayTotal}</div>
                </div>
                <div>
                  <span className="text-emerald-200 text-xs uppercase">Total MTD</span>
                  <div className="text-2xl font-bold tabular-nums">{ipdMtdTotal}</div>
                </div>
              </div>
              <div className="mt-4 max-h-52 overflow-y-auto rounded-lg bg-white/10 ring-1 ring-white/15">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-emerald-900/90 text-emerald-100">
                    <tr>
                      <th className="py-2 px-3 text-left font-medium">Source</th>
                      <th className="py-2 px-2 text-right font-medium">Today</th>
                      <th className="py-2 px-3 text-right font-medium">MTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceRows.map((row) => (
                      <tr key={`ipd-${row.source}`} className="border-t border-white/10">
                        <td className="py-1.5 px-3 font-medium">{formatLeadSourceLabel(row.source)}</td>
                        <td className="py-1.5 px-2 text-right tabular-nums">{row.ipdToday}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums font-semibold">{row.ipdMtd}</td>
                      </tr>
                    ))}
                    {sourceRows.length === 0 && (
                      <tr>
                        <td colSpan={3} className="py-4 px-3 text-center text-emerald-200/80">
                          No discharged IPD from CRM leads in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="rounded-xl border bg-white p-4 shadow-sm overflow-x-auto">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">Team · CRM lead billing</div>
                <p className="text-xs text-slate-500 mt-0.5">
                  One count per customer (phone); credited to lead owner (latest lead if duplicates). OPD bill = consultation bill date. IPD = discharged admission from CRM lead. Sarvam columns = cumulative all-time from Sarvam OPD booking funnel; Agent 2 = appointment reminder confirmed original slot. Exotel source excluded.
                </p>
              </div>
              {crmBilling?.period?.istDateLabel && (
                <div className="text-xs text-slate-500">
                  IST · today {crmBilling.period.istDateLabel} · MTD {crmBilling.period.istMonthLabel}
                </div>
              )}
            </div>
            <table className="min-w-full text-sm mt-3">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="py-2 text-left font-medium">Rep</th>
                  <th className="py-2 text-right font-medium">OPD bill today</th>
                  <th className="py-2 text-right font-medium">OPD bill MTD</th>
                  <th className="py-2 text-right font-medium">IPD today</th>
                  <th className="py-2 text-right font-medium">IPD MTD</th>
                  <th className="py-2 text-right font-medium">Sarvam booked OPDs</th>
                  <th className="py-2 text-right font-medium">Sarvam visited OPDs</th>
                  <th className="py-2 text-right font-medium">Agent 2 reminder confirmed</th>
                  <th className="py-2 text-right font-medium">Sarvam IPDs</th>
                </tr>
              </thead>
              <tbody>
                {(crmBilling?.reps ?? []).map((row: any) => {
                  const counts = billingCounts(row);
                  const repLabel = row.rep?.name ?? 'Rep';
                  const isExitedRow = isExitedEmployeesBucketId(row.rep?.id);
                  return (
                    <tr
                      key={row.rep.id}
                      className={`border-b ${
                        row.rep.id === user?.id
                          ? 'bg-indigo-50/80'
                          : isExitedRow
                            ? 'bg-amber-50/50'
                            : ''
                      }`}
                    >
                      <td className="py-2 pr-3">
                        <div className="font-medium text-slate-900">{row.rep.name}</div>
                        {row.rep.email ? (
                          <div className="text-xs text-slate-500">{row.rep.email}</div>
                        ) : null}
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.opdBillToday ?? 0}
                          metric="opdBillToday"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.opdBillMtd ?? 0}
                          metric="opdBillMtd"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.ipdToday ?? 0}
                          metric="ipdToday"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.ipdMtd ?? 0}
                          metric="ipdMtd"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                          emphasize
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.sarvamOpdBooked ?? 0}
                          metric="sarvamOpdBooked"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.sarvamOpdVisited ?? 0}
                          metric="sarvamOpdVisited"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.sarvamReminderConfirmed ?? 0}
                          metric="sarvamReminderConfirmed"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <BillingCountCell
                          value={counts.sarvamIpd ?? 0}
                          metric="sarvamIpd"
                          repLabel={repLabel}
                          staffId={row.rep.id}
                          emphasize
                        />
                      </td>
                    </tr>
                  );
                })}
                {(crmBilling?.reps ?? []).length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-6 text-center text-slate-500">
                      No salespeople or still loading.
                    </td>
                  </tr>
                )}
              </tbody>
              {(crmBilling?.reps ?? []).length > 0 && user?.role !== 'SALES_PERSON' && (
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold text-slate-900">
                    <td className="py-2.5 pr-3">Total</td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.opdBillToday ?? 0}
                        metric="opdBillToday"
                        repLabel="All reps"
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.opdBillMtd ?? 0}
                        metric="opdBillMtd"
                        repLabel="All reps"
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.ipdToday ?? 0}
                        metric="ipdToday"
                        repLabel="All reps"
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.ipdMtd ?? 0}
                        metric="ipdMtd"
                        repLabel="All reps"
                        emphasize
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.sarvamOpdBooked ?? 0}
                        metric="sarvamOpdBooked"
                        repLabel="All reps"
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.sarvamOpdVisited ?? 0}
                        metric="sarvamOpdVisited"
                        repLabel="All reps"
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.sarvamReminderConfirmed ?? 0}
                        metric="sarvamReminderConfirmed"
                        repLabel="All reps"
                      />
                    </td>
                    <td className="py-2.5 text-right">
                      <BillingCountCell
                        value={teamTotals.sarvamIpd ?? 0}
                        metric="sarvamIpd"
                        repLabel="All reps"
                        emphasize
                      />
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <Dialog
            open={!!billingDrill}
            onOpenChange={(open) => {
              if (!open) {
                setBillingDrill(null);
                setBillingDrillLead(null);
              }
            }}
          >
            <DialogContent className="flex max-h-[min(88vh,720px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
              <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
                <DialogTitle>
                  {billingDrill
                    ? `${CRM_BILLING_METRIC_LABELS[billingDrill.metric]} · ${billingDrill.repLabel}`
                    : 'CRM leads'}
                </DialogTitle>
                <DialogDescription>
                  Leads counted in this metric (one per customer phone). Click a row to open lead detail.
                </DialogDescription>
              </DialogHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                {billingDrillLoading ? (
                  <div className="py-8 text-center text-sm text-slate-500">Loading leads…</div>
                ) : billingDrillLeads.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-500">No leads for this metric.</div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b text-xs text-slate-500">
                        <th className="py-2 text-left font-medium">Patient</th>
                        <th className="py-2 text-left font-medium">Owner</th>
                        <th className="py-2 text-left font-medium">Source</th>
                        <th className="py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingDrillLeads.map((lead) => (
                        <tr
                          key={lead.id}
                          className="border-b last:border-b-0 hover:bg-slate-50 cursor-pointer"
                          onClick={() => setBillingDrillLead(lead)}
                        >
                          <td className="py-2 pr-3">
                            <div className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900">{lead.patientName}</div>
                                <div className="text-xs text-slate-500">
                                  {shouldMaskLeadPhone(user)
                                    ? maskPhoneDisplay(lead.phone)
                                    : lead.phone}
                                </div>
                              </div>
                              {!shouldMaskLeadPhone(user) && !agencyViewOnly ? (
                                <LeadQuickCallButton
                                  leadId={lead.id}
                                  phone={lead.phone}
                                  patientName={lead.patientName}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 text-slate-700">{lead.assignedTo?.name ?? '—'}</td>
                          <td className="py-2 text-slate-700">{formatLeadSourceLabel(lead.source)}</td>
                          <td className="py-2 text-slate-700">
                            {formatLeadStatusLabel(lead.status)}
                            {lead.leadSubStatus
                              ? ` · ${formatLeadSubStatusLineWithDnp(
                                  lead.leadSubStatus,
                                  lead.notReachableAttemptTotal
                                )}`
                              : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <LeadDetailModal
            open={!!billingDrillLead}
            onOpenChange={(open) => {
              if (!open) setBillingDrillLead(null);
            }}
            anchorLead={billingDrillLead}
            navLeads={billingDrillLeads}
          />
        </div>
        );
      })()}

      {showManagerDashboard && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { label: 'Today', data: mgrToday },
              { label: 'Week', data: mgrWeek },
              { label: 'Month', data: mgrMonth },
            ].map((box) => (
              <div key={box.label} className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{box.label}</div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-slate-500 text-xs">Leads</div>
                    <div className="text-lg font-semibold text-slate-900">{box.data?.createdTotal ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">Conversions</div>
                    <div className="text-lg font-semibold text-slate-900">{box.data?.convertedInPeriod ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">Open</div>
                    <div className="text-lg font-semibold text-slate-900">{box.data?.openCount ?? '—'}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">Conv %</div>
                    <div className="text-lg font-semibold text-slate-900">{box.data?.conversionRatePercent != null ? `${box.data.conversionRatePercent}%` : '—'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 rounded-xl border bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Team performance</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    By lead owner · Open pipeline is current snapshot · OPD visited and AI metrics use the date range (max {TEAM_PERF_MAX_DAYS} days, IST)
                  </div>
                  {mgrRep?.period?.clamped ? (
                    <div className="text-xs text-amber-700 mt-1">
                      Range was shortened to the last {TEAM_PERF_MAX_DAYS} days.
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-end gap-2 shrink-0">
                  <label className="text-xs text-slate-600">
                    From
                    <input
                      type="date"
                      className="mt-1 block rounded-md border border-slate-200 px-2 py-1 text-sm"
                      value={mgrTeamRange.from}
                      max={mgrTeamRange.to}
                      onChange={(e) => {
                        const next = clampManagerTeamPerfRange(e.target.value, mgrTeamRange.to);
                        setMgrTeamRange(next);
                      }}
                    />
                  </label>
                  <label className="text-xs text-slate-600">
                    To
                    <input
                      type="date"
                      className="mt-1 block rounded-md border border-slate-200 px-2 py-1 text-sm"
                      value={mgrTeamRange.to}
                      min={mgrTeamRange.from}
                      onChange={(e) => {
                        const next = clampManagerTeamPerfRange(mgrTeamRange.from, e.target.value);
                        setMgrTeamRange(next);
                      }}
                    />
                  </label>
                </div>
              </div>
              {(() => {
                const openTeamPerfDrill = (
                  metric: ManagerTeamPerfMetricKey,
                  repId: string,
                  repLabel: string,
                ) => {
                  setTeamPerfDrill({ metric, repId, repLabel });
                };

                type TeamPerfMetricTotals = {
                  openLeads: number;
                  contacted: number;
                  interested: number;
                  followUp: number;
                  opdVisited: number;
                  missedFollowUps: number;
                  dnpMissed: number;
                  aiRevivedDnp: number;
                  aiBookedOpd: number;
                  aiMarkedLost: number;
                };

                const emptyTeamPerfTotals: TeamPerfMetricTotals = {
                  openLeads: 0,
                  contacted: 0,
                  interested: 0,
                  followUp: 0,
                  opdVisited: 0,
                  missedFollowUps: 0,
                  dnpMissed: 0,
                  aiRevivedDnp: 0,
                  aiBookedOpd: 0,
                  aiMarkedLost: 0,
                };

                const teamPerfItems = (mgrRep?.items ?? []) as Array<Partial<TeamPerfMetricTotals>>;
                const teamPerfTotals = teamPerfItems.reduce<TeamPerfMetricTotals>(
                  (acc, row) => ({
                    openLeads: acc.openLeads + (row.openLeads ?? 0),
                    contacted: acc.contacted + (row.contacted ?? 0),
                    interested: acc.interested + (row.interested ?? 0),
                    followUp: acc.followUp + (row.followUp ?? 0),
                    opdVisited: acc.opdVisited + (row.opdVisited ?? 0),
                    missedFollowUps: acc.missedFollowUps + (row.missedFollowUps ?? 0),
                    dnpMissed: acc.dnpMissed + (row.dnpMissed ?? 0),
                    aiRevivedDnp: acc.aiRevivedDnp + (row.aiRevivedDnp ?? 0),
                    aiBookedOpd: acc.aiBookedOpd + (row.aiBookedOpd ?? 0),
                    aiMarkedLost: acc.aiMarkedLost + (row.aiMarkedLost ?? 0),
                  }),
                  emptyTeamPerfTotals,
                );

                const TeamPerfCountCell = ({
                  value,
                  metric,
                  repId,
                  repLabel,
                  tone = 'default',
                }: {
                  value: number;
                  metric: ManagerTeamPerfMetricKey;
                  repId: string;
                  repLabel: string;
                  tone?: 'default' | 'warn' | 'danger' | 'success';
                }) => {
                  const toneClass =
                    tone === 'warn'
                      ? 'text-amber-800'
                      : tone === 'danger'
                        ? 'text-rose-800'
                        : tone === 'success'
                          ? 'text-emerald-800'
                          : 'text-indigo-700';
                  return (
                    <button
                      type="button"
                      className={`tabular-nums font-medium hover:underline ${toneClass}`}
                      title="View leads"
                      onClick={() => openTeamPerfDrill(metric, repId, repLabel)}
                    >
                      {value}
                    </button>
                  );
                };

                return (
              <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 border-b">
                    <th className="py-2 text-left font-medium sticky left-0 bg-white">Lead owner</th>
                    <th className="py-2 text-right font-medium">Open</th>
                    <th className="py-2 text-right font-medium">Contacted</th>
                    <th className="py-2 text-right font-medium">Interested</th>
                    <th className="py-2 text-right font-medium">Follow up</th>
                    <th className="py-2 text-right font-medium">OPD visited</th>
                    <th className="py-2 text-right font-medium">Follow-ups missed</th>
                    <th className="py-2 text-right font-medium">DNP missed</th>
                    <th className="py-2 text-right font-medium">AI revived DNP</th>
                    <th className="py-2 text-right font-medium">AI booked OPD</th>
                    <th className="py-2 text-right font-medium">AI marked lost</th>
                  </tr>
                </thead>
                <tbody>
                  {mgrRepLoading ? (
                    <tr>
                      <td colSpan={11} className="py-6 text-center text-slate-500">
                        Loading team performance…
                      </td>
                    </tr>
                  ) : mgrRepError ? (
                    <tr>
                      <td colSpan={11} className="py-6 text-center text-rose-600">
                        Could not load team performance. Refresh the page or contact support.
                      </td>
                    </tr>
                  ) : null}
                  {!mgrRepLoading && !mgrRepError && (mgrRep?.items ?? []).map((row: any) => {
                    const isExitedRow = isExitedEmployeesBucketId(row.rep?.id);
                    const repId = row.rep.id as string;
                    const repLabel = row.rep.name as string;
                    return (
                    <tr
                      key={row.rep.id}
                      className={`border-b last:border-b-0 ${isExitedRow ? 'bg-amber-50/50' : ''}`}
                    >
                      <td className="py-2 pr-3 sticky left-0 bg-inherit">
                        <div className="font-medium text-slate-900">{row.rep.name}</div>
                        {row.rep.email ? (
                          <div className="text-xs text-slate-500">{row.rep.email}</div>
                        ) : null}
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.openLeads ?? 0} metric="open_leads" repId={repId} repLabel={repLabel} />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.contacted ?? 0} metric="contacted" repId={repId} repLabel={repLabel} />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.interested ?? 0} metric="interested" repId={repId} repLabel={repLabel} />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.followUp ?? 0} metric="follow_up" repId={repId} repLabel={repLabel} />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.opdVisited ?? 0} metric="opd_visited" repId={repId} repLabel={repLabel} tone="success" />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.missedFollowUps ?? 0} metric="follow_ups_missed" repId={repId} repLabel={repLabel} tone="warn" />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.dnpMissed ?? 0} metric="dnp_missed" repId={repId} repLabel={repLabel} tone="danger" />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.aiRevivedDnp ?? 0} metric="ai_revived_dnp" repId={repId} repLabel={repLabel} />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.aiBookedOpd ?? 0} metric="ai_booked_opd" repId={repId} repLabel={repLabel} tone="success" />
                      </td>
                      <td className="py-2 text-right">
                        <TeamPerfCountCell value={row.aiMarkedLost ?? 0} metric="ai_marked_lost" repId={repId} repLabel={repLabel} tone="danger" />
                      </td>
                    </tr>
                    );
                  })}
                  {!mgrRepLoading && !mgrRepError && (mgrRep?.items ?? []).length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-6 text-center text-slate-500">No lead owners or no data.</td>
                    </tr>
                  )}
                  {!mgrRepLoading && !mgrRepError && teamPerfItems.length > 0 && (
                    <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold text-slate-900">
                      <td className="py-2.5 pr-3 sticky left-0 bg-slate-100">Total</td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.openLeads}
                          metric="open_leads"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.contacted}
                          metric="contacted"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.interested}
                          metric="interested"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.followUp}
                          metric="follow_up"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.opdVisited}
                          metric="opd_visited"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                          tone="success"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.missedFollowUps}
                          metric="follow_ups_missed"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                          tone="warn"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.dnpMissed}
                          metric="dnp_missed"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                          tone="danger"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.aiRevivedDnp}
                          metric="ai_revived_dnp"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.aiBookedOpd}
                          metric="ai_booked_opd"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                          tone="success"
                        />
                      </td>
                      <td className="py-2.5 text-right">
                        <TeamPerfCountCell
                          value={teamPerfTotals.aiMarkedLost}
                          metric="ai_marked_lost"
                          repId={MANAGER_TEAM_PERF_ALL_REPS_ID}
                          repLabel="All reps"
                          tone="danger"
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
                );
              })()}
            </div>

            <div className="rounded-xl border bg-white p-4 shadow-sm space-y-4">
              <div className="rounded-lg border bg-slate-50 p-3">
                <div className="text-sm font-semibold text-slate-900">Funnel (open pipeline)</div>
                <div className="mt-3 space-y-2">
                  {[
                    { key: 'NEW', label: 'New' },
                    { key: 'CONTACTED', label: 'Contacted' },
                    { key: 'INTERESTED', label: 'Interested' },
                    { key: 'FOLLOW_UP', label: 'Follow-up' },
                    { key: 'CONVERTED', label: 'Converted' },
                    { key: 'LOST', label: 'Lost' },
                    { key: 'CLOSED', label: 'Closed' },
                  ].map((s) => {
                    const counts = (mgrMonth?.stageCounts ?? {}) as Record<string, number>;
                    const v = counts[s.key] ?? 0;
                    const openMax = Math.max(
                      1,
                      ...['NEW', 'CONTACTED', 'INTERESTED', 'FOLLOW_UP'].map((k) => counts[k] ?? 0),
                    );
                    const pct = Math.min(100, Math.round((v / openMax) * 100));
                    const prevKey =
                      s.key === 'CONTACTED'
                        ? 'NEW'
                        : s.key === 'INTERESTED'
                          ? 'CONTACTED'
                          : s.key === 'FOLLOW_UP'
                            ? 'INTERESTED'
                            : null;
                    const prevVal = prevKey ? (counts[prevKey] ?? 0) : null;
                    const stepPct =
                      prevVal && prevVal > 0 ? Math.round((v / prevVal) * 100) : null;
                    return (
                      <div key={s.key} className="flex items-center gap-2">
                        <div className="w-24 text-xs text-slate-600">{s.label}</div>
                        <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                          <div
                            className={`h-2 rounded-full ${
                              s.key === 'CONVERTED'
                                ? 'bg-emerald-500'
                                : s.key === 'LOST'
                                  ? 'bg-rose-400'
                                  : s.key === 'CLOSED'
                                    ? 'bg-slate-400'
                                    : 'bg-indigo-500'
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="w-10 text-right text-xs font-semibold text-slate-800">{v}</div>
                        <div className="w-12 text-right text-[10px] text-slate-500">
                          {stepPct != null ? `${stepPct}%` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">Missed follow-ups</div>
                  {canManageTeamLeads && !agencyViewOnly && missedSelected.length > 0 && (
                    <div className="text-xs text-slate-600">{missedSelected.length} selected</div>
                  )}
                </div>
                {canManageTeamLeads && !agencyViewOnly && missedSelected.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Select
                      value={missedBulkAssignToId || undefined}
                      onValueChange={(v) => setMissedBulkAssignToId(v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger className="h-8 w-[11.5rem] text-xs">
                        <SelectValue placeholder="Assign to…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {staffOptions.map((s: any) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      className="h-8"
                      disabled={bulkAssignMut.isPending}
                      onClick={() => {
                        setAssignmentReasonInput('');
                        setPendingAssign({
                          mode: 'bulk',
                          leadIds: missedSelected,
                          assignedToId: missedBulkAssignToId ? missedBulkAssignToId : null,
                        });
                      }}
                    >
                      Bulk assign
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setMissedSelectedIds({})}>
                      Clear
                    </Button>
                  </div>
                )}
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-1">
                  {(mgrAlerts?.missedFollowUps ?? []).map((l: any) => (
                    <div key={l.id} className="rounded-lg border px-3 py-2 hover:bg-slate-50">
                      {canManageTeamLeads && !agencyViewOnly && (
                        <div className="flex items-center justify-between pb-1">
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={!!missedSelectedIds[l.id]}
                              onChange={(e) =>
                                setMissedSelectedIds((p) => ({ ...p, [l.id]: e.target.checked }))
                              }
                            />
                            Select
                          </label>
                          <button
                            type="button"
                            className="text-[10px] text-slate-500 hover:underline"
                            onClick={() => {
                              const all = (mgrAlerts?.missedFollowUps ?? []).map((x: any) => x.id);
                              const allSelected = all.length > 0 && all.every((id: string) => missedSelectedIds[id]);
                              setMissedSelectedIds(() => {
                                const next: Record<string, boolean> = {};
                                for (const id of all) next[id] = !allSelected;
                                return next;
                              });
                            }}
                          >
                            Toggle all
                          </button>
                        </div>
                      )}
                      <button type="button" className="w-full text-left" onClick={() => openLeadDetail(l)}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-slate-900 truncate">{l.patientName}</div>
                          <div className="text-xs text-slate-500 shrink-0">{l.assignedTo?.name ?? 'Unassigned'}</div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {l.nextFollowUpAt ? new Date(l.nextFollowUpAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}
                        </div>
                      </button>
                      {canManageTeamLeads && !agencyViewOnly && (
                        <div className="mt-2 flex items-center gap-2">
                          <Select
                            value={alertAssignDraft[l.id] ?? '__keep__'}
                            onValueChange={(v) => setAlertAssignDraft((p) => ({ ...p, [l.id]: v }))}
                          >
                            <SelectTrigger className="h-8 w-[11.5rem] text-xs">
                              <SelectValue placeholder="Reassign…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__keep__">Reassign…</SelectItem>
                              <SelectItem value="__none__">Unassigned</SelectItem>
                              {staffOptions.map((s: any) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={
                              assignLeadMut.isPending ||
                              !alertAssignDraft[l.id] ||
                              alertAssignDraft[l.id] === '__keep__'
                            }
                            onClick={() => {
                              const v = alertAssignDraft[l.id];
                              if (!v || v === '__keep__') return;
                              setAssignmentReasonInput('');
                              setPendingAssign({
                                mode: 'single',
                                leadId: l.id,
                                assignedToId: v === '__none__' ? null : v,
                              });
                            }}
                          >
                            Assign
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {(mgrAlerts?.missedFollowUps ?? []).length === 0 && (
                    <div className="text-sm text-slate-500">None</div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">No activity (24h)</div>
                  {canManageTeamLeads && !agencyViewOnly && noActSelected.length > 0 && (
                    <div className="text-xs text-slate-600">{noActSelected.length} selected</div>
                  )}
                </div>
                {canManageTeamLeads && !agencyViewOnly && noActSelected.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Select
                      value={noActBulkAssignToId || undefined}
                      onValueChange={(v) => setNoActBulkAssignToId(v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger className="h-8 w-[11.5rem] text-xs">
                        <SelectValue placeholder="Assign to…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {staffOptions.map((s: any) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      className="h-8"
                      disabled={bulkAssignMut.isPending}
                      onClick={() => {
                        setAssignmentReasonInput('');
                        setPendingAssign({
                          mode: 'bulk',
                          leadIds: noActSelected,
                          assignedToId: noActBulkAssignToId ? noActBulkAssignToId : null,
                        });
                      }}
                    >
                      Bulk assign
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => setNoActSelectedIds({})}>
                      Clear
                    </Button>
                  </div>
                )}
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-1">
                  {(mgrAlerts?.noActivity24h ?? []).map((l: any) => (
                    <div key={l.id} className="rounded-lg border px-3 py-2 hover:bg-slate-50">
                      {canManageTeamLeads && !agencyViewOnly && (
                        <div className="flex items-center justify-between pb-1">
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={!!noActSelectedIds[l.id]}
                              onChange={(e) =>
                                setNoActSelectedIds((p) => ({ ...p, [l.id]: e.target.checked }))
                              }
                            />
                            Select
                          </label>
                          <button
                            type="button"
                            className="text-[10px] text-slate-500 hover:underline"
                            onClick={() => {
                              const all = (mgrAlerts?.noActivity24h ?? []).map((x: any) => x.id);
                              const allSelected = all.length > 0 && all.every((id: string) => noActSelectedIds[id]);
                              setNoActSelectedIds(() => {
                                const next: Record<string, boolean> = {};
                                for (const id of all) next[id] = !allSelected;
                                return next;
                              });
                            }}
                          >
                            Toggle all
                          </button>
                        </div>
                      )}
                      <button type="button" className="w-full text-left" onClick={() => openLeadDetail(l)}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-slate-900 truncate">{l.patientName}</div>
                          <div className="text-xs text-slate-500 shrink-0">{l.assignedTo?.name ?? 'Unassigned'}</div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Updated {new Date(l.updatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </div>
                      </button>
                      {canManageTeamLeads && !agencyViewOnly && (
                        <div className="mt-2 flex items-center gap-2">
                          <Select
                            value={alertAssignDraft[l.id] ?? '__keep__'}
                            onValueChange={(v) => setAlertAssignDraft((p) => ({ ...p, [l.id]: v }))}
                          >
                            <SelectTrigger className="h-8 w-[11.5rem] text-xs">
                              <SelectValue placeholder="Reassign…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__keep__">Reassign…</SelectItem>
                              <SelectItem value="__none__">Unassigned</SelectItem>
                              {staffOptions.map((s: any) => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={
                              assignLeadMut.isPending ||
                              !alertAssignDraft[l.id] ||
                              alertAssignDraft[l.id] === '__keep__'
                            }
                            onClick={() => {
                              const v = alertAssignDraft[l.id];
                              if (!v || v === '__keep__') return;
                              setAssignmentReasonInput('');
                              setPendingAssign({
                                mode: 'single',
                                leadId: l.id,
                                assignedToId: v === '__none__' ? null : v,
                              });
                            }}
                          >
                            Assign
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {(mgrAlerts?.noActivity24h ?? []).length === 0 && (
                    <div className="text-sm text-slate-500">None</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Dialog
            open={!!teamPerfDrill}
            onOpenChange={(open) => {
              if (!open) {
                setTeamPerfDrill(null);
                setTeamPerfDrillLead(null);
              }
            }}
          >
            <DialogContent className="flex max-h-[min(88vh,720px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
              <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
                <DialogTitle>
                  {teamPerfDrill
                    ? `${MANAGER_TEAM_PERF_METRIC_LABELS[teamPerfDrill.metric]} · ${teamPerfDrill.repLabel}`
                    : 'Team performance leads'}
                </DialogTitle>
                <DialogDescription>
                  {teamPerfRange.from} – {teamPerfRange.to} (IST). Click a row to open lead detail.
                </DialogDescription>
              </DialogHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                {teamPerfDrillLoading ? (
                  <div className="py-8 text-center text-sm text-slate-500">Loading leads…</div>
                ) : teamPerfDrillLeads.length === 0 ? (
                  <div className="py-8 text-center text-sm text-slate-500">No leads for this metric.</div>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b text-xs text-slate-500">
                        <th className="py-2 text-left font-medium">Patient</th>
                        <th className="py-2 text-left font-medium">Owner</th>
                        <th className="py-2 text-left font-medium">Source</th>
                        <th className="py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamPerfDrillLeads.map((lead) => (
                        <tr
                          key={lead.id}
                          className="border-b last:border-b-0 hover:bg-slate-50 cursor-pointer"
                          onClick={() => setTeamPerfDrillLead(lead)}
                        >
                          <td className="py-2 pr-3">
                            <div className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-slate-900">{lead.patientName}</div>
                                <div className="text-xs text-slate-500">
                                  {shouldMaskLeadPhone(user)
                                    ? maskPhoneDisplay(lead.phone)
                                    : lead.phone}
                                </div>
                              </div>
                              {!shouldMaskLeadPhone(user) && !agencyViewOnly ? (
                                <LeadQuickCallButton
                                  leadId={lead.id}
                                  phone={lead.phone}
                                  patientName={lead.patientName}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className="py-2 text-slate-700">{lead.assignedTo?.name ?? '—'}</td>
                          <td className="py-2 text-slate-700">{formatLeadSourceLabel(lead.source)}</td>
                          <td className="py-2 text-slate-700">
                            {formatLeadStatusLabel(lead.status)}
                            {lead.leadSubStatus
                              ? ` · ${formatLeadSubStatusLineWithDnp(
                                  lead.leadSubStatus,
                                  lead.notReachableAttemptTotal
                                )}`
                              : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <LeadDetailModal
            open={!!teamPerfDrillLead}
            onOpenChange={(open) => {
              if (!open) setTeamPerfDrillLead(null);
            }}
            anchorLead={teamPerfDrillLead}
            navLeads={teamPerfDrillLeads}
          />
        </div>
      )}

      <Dialog
        open={!!pendingAssign}
        onOpenChange={(open) => {
          if (!open) {
            setPendingAssign(null);
            setAssignmentReasonInput('');
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reason for assignment</DialogTitle>
            <DialogDescription>
              {pendingAssign?.mode === 'bulk'
                ? `Reassigning ${pendingAssign.leadIds.length} lead(s). This is recorded in the assignment audit log.`
                : 'Explain why this lead is being reassigned. This is recorded in the assignment audit log.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="assignment-reason">Reason</Label>
            <Textarea
              id="assignment-reason"
              value={assignmentReasonInput}
              onChange={(e) => setAssignmentReasonInput(e.target.value)}
              placeholder="e.g. workload balance, specialist follow-up, owner on leave…"
              rows={4}
              maxLength={2000}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingAssign(null)}>
              Cancel
            </Button>
            <Button
              disabled={assignLeadMut.isPending || bulkAssignMut.isPending}
              onClick={submitPendingAssign}
            >
              {assignLeadMut.isPending || bulkAssignMut.isPending ? 'Saving…' : 'Confirm assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canManageTeamLeads && !agencyViewOnly && selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-white px-4 py-3 shadow-sm">
          <div className="text-sm text-slate-700">
            <span className="font-semibold">{selectedIds.length}</span> selected
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={bulkAssignToId || undefined} onValueChange={(v) => setBulkAssignToId(v === '__none__' ? '' : v)}>
              <SelectTrigger className="w-[14rem] h-9 text-sm">
                <SelectValue placeholder="Assign to…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {staffOptions.map((s: any) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              disabled={bulkAssignMut.isPending}
              onClick={() => {
                setAssignmentReasonInput('');
                setPendingAssign({
                  mode: 'bulk',
                  leadIds: selectedIds,
                  assignedToId: bulkAssignToId ? bulkAssignToId : null,
                });
              }}
            >
              {bulkAssignMut.isPending ? 'Assigning…' : 'Bulk assign'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSelectedLeadIds({})}>
              Clear
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2 text-sm flex-wrap">
          <button
            type="button"
            className={`px-3 py-1 rounded-full border ${statusFilters.length === 0 && !sarvamCalledFilter ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-700'}`}
            onClick={() => {
              setStatusFilters([]);
              setSubStatusFilters([]);
              setSarvamCalledFilter(false);
            }}
          >
            All
          </button>
          {LEAD_STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className={`px-3 py-1 rounded-full border text-xs transition-colors ${
                statusFilters.includes(s)
                  ? 'bg-blue-600 text-white border-blue-600'
                  : `${leadStatusBadgeClass(s)} opacity-90 hover:opacity-100`
              }`}
              onClick={() => {
                setSarvamCalledFilter(false);
                setStatusFilters((prev) =>
                  prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
                );
              }}
            >
              {formatLeadStatusLabel(s)}
            </button>
          ))}
          <button
            type="button"
            title="Leads with at least one Sarvam AI outbound call attempt"
            className={`px-3 py-1 rounded-full border text-xs transition-colors ${
              sarvamCalledFilter
                ? 'bg-violet-600 text-white border-violet-600'
                : 'border-violet-300 bg-violet-50 text-violet-800 opacity-90 hover:opacity-100'
            }`}
            onClick={() => {
              setStatusFilters([]);
              setSubStatusFilters([]);
              setSarvamCalledFilter((prev) => !prev);
            }}
          >
            Sarvam called
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canManageTeamLeads && (
            <Select value={ownerFilter} onValueChange={(v) => setOwnerFilter(v)}>
              <SelectTrigger className="w-[12rem] h-9 text-sm">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__all__">All owners</SelectItem>
                <SelectItem value="me">My leads</SelectItem>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {staffOptions.map((s: any) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <FilterMultiSelect
            values={sourceFilters}
            onValuesChange={setSourceFilters}
            options={sourceFilterOptions}
            placeholder="Source"
            allLabel="All sources"
            triggerClassName="w-[10.5rem]"
          />
          <FilterMultiSelect
            values={campaignFilters}
            onValuesChange={setCampaignFilters}
            options={campaignFilterOptions}
            placeholder="Campaign"
            allLabel="All campaigns"
            triggerClassName="w-[12rem]"
          />
          <Select
            value={agencyFilter || '__all__'}
            onValueChange={(v) => setAgencyFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-[11rem] h-9 text-sm">
              <SelectValue placeholder="Agency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All agencies</SelectItem>
              <SelectItem value="__none__">No agency</SelectItem>
              {(leadFilterOptions?.agencyKeys ?? []).map((k) => (
                <SelectItem key={k.id} value={k.id}>
                  {k.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={surgeryFilter}
            onValueChange={(v) => setSurgeryFilter(v as typeof surgeryFilter)}
          >
            <SelectTrigger className="w-[10.5rem] h-9 text-sm">
              <SelectValue placeholder="Surgery case" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All surgery</SelectItem>
              <SelectItem value="true">Surgery: Yes</SelectItem>
              <SelectItem value="false">Surgery: No</SelectItem>
              <SelectItem value="unset">Not specified</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={bantTierFilter}
            onValueChange={(v) => {
              setBantTierFilter(v as BantTierFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[11rem] h-9 text-sm">
              <SelectValue placeholder="Lead score" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All lead scores</SelectItem>
              {BANT_TIER_OPTIONS.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
              <SelectItem value="__unscored__">Unscored</SelectItem>
            </SelectContent>
          </Select>
          <FilterMultiSelect
            values={subStatusFilters}
            onValuesChange={(values) =>
              setSubStatusFilters(values as Array<LeadSubStatus | '__none__'>)
            }
            options={subStatusFilterOptions}
            placeholder="Sub-status"
            allLabel="All sub-statuses"
            triggerClassName="w-[13rem]"
          />
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[11px] text-slate-500">Created from</Label>
              <Input
                type="date"
                value={createdFrom}
                onChange={(e) => {
                  setCreatedFrom(e.target.value);
                  setPage(1);
                }}
                className="w-[9.5rem] h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Time</Label>
              <Input
                type="time"
                value={createdFromTime}
                onChange={(e) => {
                  setCreatedFromTime(e.target.value);
                  setPage(1);
                }}
                className="w-[6.5rem] h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Created to</Label>
              <Input
                type="date"
                value={createdTo}
                onChange={(e) => {
                  setCreatedTo(e.target.value);
                  setPage(1);
                }}
                className="w-[9.5rem] h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Time</Label>
              <Input
                type="time"
                value={createdToTime}
                onChange={(e) => {
                  setCreatedToTime(e.target.value);
                  setPage(1);
                }}
                className="w-[6.5rem] h-9"
              />
            </div>
            {(createdFrom || createdTo || createdFromTime || createdToTime) && (
              <Button
                type="button"
                variant="outline"
                className="h-9"
                onClick={() => {
                  setCreatedFrom('');
                  setCreatedTo('');
                  setCreatedFromTime('');
                  setCreatedToTime('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[11px] text-slate-500">Follow up from</Label>
              <Input
                type="date"
                value={followUpFrom}
                onChange={(e) => {
                  setFollowUpFrom(e.target.value);
                  setPage(1);
                }}
                className="w-[9.5rem] h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Time</Label>
              <Input
                type="time"
                value={followUpFromTime}
                onChange={(e) => {
                  setFollowUpFromTime(e.target.value);
                  setPage(1);
                }}
                className="w-[6.5rem] h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Follow up to</Label>
              <Input
                type="date"
                value={followUpTo}
                onChange={(e) => {
                  setFollowUpTo(e.target.value);
                  setPage(1);
                }}
                className="w-[9.5rem] h-9"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Time</Label>
              <Input
                type="time"
                value={followUpToTime}
                onChange={(e) => {
                  setFollowUpToTime(e.target.value);
                  setPage(1);
                }}
                className="w-[6.5rem] h-9"
              />
            </div>
            {(followUpFrom || followUpTo || followUpFromTime || followUpToTime) && (
              <Button
                type="button"
                variant="outline"
                className="h-9"
                onClick={() => {
                  setFollowUpFrom('');
                  setFollowUpTo('');
                  setFollowUpFromTime('');
                  setFollowUpToTime('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
          <Input
            placeholder="Search by name or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 h-9"
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        List shows one row per phone number (latest record). A repeat enquiry resets the row to{' '}
        <span className="font-medium">New</span> and shows the previous status on the line below. New
        ingests while a lead is already open also restart the cycle. After Lost / Converted / Closed, the
        same phone creates a new lead record. Open history to see status, owner, and activities across all
        records for that number. Phone search uses the{' '}
        <span className="font-medium">last 10 digits</span> (so +91 / spacing still match). Add an{' '}
        <span className="font-medium">additional number</span> on a lead to search either contact.
      </p>
      {sourceFilters.length ? (
        <p className="text-xs text-amber-900/90 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-2 -mt-0.5">
          <span className="font-medium">Search tip:</span> Source is limited to{' '}
          <strong>{sourceFilters.join(', ')}</strong>. Clear source filters if you expect the lead on
          another channel (e.g. Practo vs Exotel). Filters combine with search.
        </p>
      ) : null}

      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {canManageTeamLeads && !agencyViewOnly && (
                <th className="px-3 py-2 text-left font-medium text-gray-700 w-10">
                  <input
                    type="checkbox"
                    checked={leads.length > 0 && selectedIds.length === leads.length}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedLeadIds((prev) => {
                        const next: Record<string, boolean> = { ...prev };
                        for (const l of leads) next[l.id] = checked;
                        return next;
                      });
                    }}
                  />
                </th>
              )}
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('patientName')}>
                  Name{sortIndicator('patientName')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('phone')}>
                  Phone{sortIndicator('phone')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('source')}>
                  Source{sortIndicator('source')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 min-w-[8rem]">
                <button type="button" className="hover:underline" onClick={() => toggleSort('campaignName')}>
                  Campaign{sortIndicator('campaignName')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 min-w-[8.5rem]">
                <button type="button" className="hover:underline" onClick={() => toggleSort('specialisation')}>
                  Specialisation{sortIndicator('specialisation')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">
                <button type="button" className="hover:underline" onClick={() => toggleSort('isSurgeryCase')}>
                  Surgery{sortIndicator('isSurgeryCase')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700 whitespace-nowrap">
                Lead score
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('status')}>
                  Status{sortIndicator('status')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('assignedToId')}>
                  Owner{sortIndicator('assignedToId')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('nextFollowUpAt')}>
                  Next follow-up{sortIndicator('nextFollowUpAt')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('createdAt')}>
                  Created{sortIndicator('createdAt')}
                </button>
              </th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">
                <button type="button" className="hover:underline" onClick={() => toggleSort('updatedAt')}>
                  Updated{sortIndicator('updatedAt')}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={canManageTeamLeads && !agencyViewOnly ? 13 : 12} className="px-3 py-6 text-center text-gray-500">
                  Loading leads...
                </td>
              </tr>
            ) : leads.length === 0 ? (
              <tr>
                <td colSpan={canManageTeamLeads && !agencyViewOnly ? 13 : 12} className="px-3 py-6 text-center text-gray-500">
                  No leads found.
                </td>
              </tr>
            ) : (
              leads.map((lead) => {
                const showCustomerCallback = isActiveCustomerCallbackAt(
                  lead.customerCallbackAt,
                  lead.nextFollowUpAt
                );

                return (
                <tr
                  key={lead.id}
                  className={`border-t hover:bg-gray-50 ${
                    lead.lostRevivalInboundAt
                      ? 'bg-emerald-50 hover:bg-emerald-100/80 ring-2 ring-inset ring-emerald-400'
                      : lead.callNowRequestedAt
                      ? 'bg-red-50 hover:bg-red-100/80 ring-1 ring-inset ring-red-400'
                      : showCustomerCallback
                      ? 'bg-blue-50 hover:bg-blue-100/80 ring-1 ring-inset ring-blue-300'
                      : lead.frtOverdue
                      ? 'bg-red-50 hover:bg-red-100/80 ring-1 ring-inset ring-red-300'
                      : ''
                  }`}
                >
                  {canManageTeamLeads && !agencyViewOnly && (
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={!!selectedLeadIds[lead.id]}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedLeadIds((prev) => ({ ...prev, [lead.id]: checked }));
                        }}
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="text-left text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
                      onClick={() => openLeadDetail(lead)}
                    >
                      {lead.patientName}
                    </button>
                    {lead.websiteOpdAppointmentId ? (
                      <div className="mt-0.5 text-[10px] font-semibold text-emerald-900 leading-snug">
                        Self Booked
                      </div>
                    ) : null}
                    {lead.lostRevivalInboundAt ? (
                      <div className="mt-0.5 text-[10px] font-bold text-emerald-800 leading-snug">
                        Lost revival — call immediately · {lead.source}
                      </div>
                    ) : lead.callNowRequestedAt ? (
                      <div className="mt-0.5 text-[10px] font-semibold text-red-800 leading-snug">
                        Call now requested · urgent
                      </div>
                    ) : showCustomerCallback && lead.customerCallbackAt ? (
                      <div className="mt-0.5 text-[10px] font-medium text-blue-800 leading-snug">
                        Customer callback ·{' '}
                        {new Date(lead.customerCallbackAt).toLocaleString('en-IN', {
                          timeZone: 'Asia/Kolkata',
                        })}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums">
                        {shouldMaskLeadPhone(user) ? maskPhoneDisplay(lead.phone) : lead.phone}
                      </span>
                      {!shouldMaskLeadPhone(user) && !agencyViewOnly ? (
                        <LeadQuickCallButton leadId={lead.id} phone={lead.phone} />
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex flex-col gap-0.5">
                      <span>{lead.source}</span>
                      {String(lead.source || '').trim().toUpperCase() === 'PRACTO' &&
                        lead.practoDoctorName?.trim() && (
                          <span
                            className="text-[10px] leading-snug text-slate-600 line-clamp-2"
                            title={ensureDoctorPrefix(lead.practoDoctorName)}
                          >
                            {ensureDoctorPrefix(lead.practoDoctorName)}
                          </span>
                        )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs max-w-[11rem]">
                    <span
                      className="line-clamp-2"
                      title={leadCampaignLabel(lead) || undefined}
                    >
                      {leadCampaignLabel(lead) || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs max-w-[10rem]">
                    {(() => {
                      const label = formatLeadProcedureLabel(
                        lead.specialisation,
                        lead.aestheticProcedureName
                      );
                      return (
                        <span className="line-clamp-2" title={label || undefined}>
                          {label || '—'}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {lead.isSurgeryCase === true ? 'Yes' : lead.isSurgeryCase === false ? 'No' : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs align-top">
                    {lead.bantTier || lead.weightedScore != null ? (
                      <div className="flex flex-col gap-0.5 items-start">
                        {lead.bantTier ? (
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset ${bantTierBadgeClass(lead.bantTier)}`}
                          >
                            {lead.bantTier}
                          </span>
                        ) : null}
                        {lead.weightedScore != null ? (
                          <span className="text-[11px] tabular-nums text-gray-600">
                            {Number(lead.weightedScore).toFixed(2)}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs align-top">
                    <div className="flex flex-col gap-0.5 items-start max-w-[11rem]">
                      <span className={leadStatusBadgeClass(lead.status, { frtOverdue: lead.frtOverdue })}>
                        {formatLeadStatusLabel(lead.status)}
                        {lead.frtOverdue ? ' · SLA' : ''}
                      </span>
                      <span
                        className="text-[10px] leading-snug text-gray-600 line-clamp-2"
                        title={
                          lead.status === 'NEW' && lead.priorLeadStatus
                            ? `Previous: ${formatPriorLeadStatusLine(
                                lead.priorLeadStatus,
                                lead.priorLeadSubStatus
                              )}`
                            : lead.leadSubStatus
                              ? formatLeadSubStatusLineWithDnp(
                                  lead.leadSubStatus,
                                  lead.notReachableAttemptTotal
                                )
                              : undefined
                        }
                      >
                        {lead.status === 'NEW' && lead.priorLeadStatus
                          ? `Previous: ${formatPriorLeadStatusLine(
                              lead.priorLeadStatus,
                              lead.priorLeadSubStatus
                            )}`
                          : lead.leadSubStatus
                            ? formatLeadSubStatusLineWithDnp(
                                lead.leadSubStatus,
                                lead.notReachableAttemptTotal
                              )
                            : '—'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">{lead.assignedTo?.name ?? 'Unassigned'}</td>
                  <td className="px-3 py-2 text-xs">
                    {lead.nextFollowUpAt
                      ? new Date(lead.nextFollowUpAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {new Date(lead.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {new Date(lead.updatedAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {totalLeads > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
          <span>
            Page {page} of {totalPages} · {totalLeads} lead(s)
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <LeadDetailModal
        open={detailModalOpen && !!selectedLead}
        onOpenChange={(o) => {
          setDetailModalOpen(o);
          if (!o) setSelectedLead(null);
        }}
        anchorLead={selectedLead}
        navLeads={leads}
      />


      <Dialog open={createOpen} onOpenChange={(open) => { if (!open) { setCreateOpen(false); } }}>
        <DialogContent className="flex max-h-[min(92vh,880px)] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:rounded-xl sm:w-full">
          <DialogHeader className="shrink-0 space-y-1 border-b border-slate-200/80 px-6 py-5 text-left">
            <DialogTitle className="text-lg">Add lead manually</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              Capture patient, contact person, source, and procedure details — fields scroll on smaller screens.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 text-sm">
            <div className="space-y-6">
              <div className="rounded-xl border border-slate-200/70 bg-[#FAFBFC] p-4 shadow-sm ring-1 ring-slate-200/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Patient</p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <Label className="text-xs text-slate-600">Name *</Label>
                            <Input
                      value={newLead.patientName}
                      onChange={(e) => setNewLead((prev) => ({ ...prev, patientName: e.target.value }))}
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                      autoComplete="name"
                    />
                        </div>
                        <div>
                    <Label className="text-xs text-slate-600">Phone *</Label>
                            <Input
                      value={newLead.phone}
                      onChange={(e) => setNewLead((prev) => ({ ...prev, phone: e.target.value }))}
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white font-mono shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                      inputMode="tel"
                    />
                        </div>
                        <div>
                    <Label className="text-xs text-slate-600">Alternate phone</Label>
                            <Input
                      value={newLead.alternatePhone}
                      onChange={(e) => setNewLead((prev) => ({ ...prev, alternatePhone: e.target.value }))}
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white font-mono shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                              inputMode="tel"
                      placeholder="Optional"
                            />
                        </div>
                  <div>
                    <Label className="text-xs text-slate-600">Email</Label>
                            <Input
                      type="email"
                      value={newLead.email}
                      onChange={(e) => setNewLead((prev) => ({ ...prev, email: e.target.value }))}
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                    />
                        </div>
                  <div>
                    <Label className="text-xs text-slate-600">Age</Label>
                        <Input
                      type="number"
                      min={0}
                      max={125}
                      value={newLead.ageLocal}
                      onChange={(e) => setNewLead((prev) => ({ ...prev, ageLocal: e.target.value }))}
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                      placeholder="Years"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Gender</Label>
                        <Select
                      value={newLead.gender}
                          onValueChange={(v) =>
                        setNewLead((prev) => ({
                          ...prev,
                          gender: v as NewLeadForm['gender'],
                        }))
                      }
                    >
                      <SelectTrigger className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus:ring-2 focus:ring-indigo-500/30">
                        <SelectValue placeholder="Not specified" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unset">Not specified</SelectItem>
                        <SelectItem value="MALE">Male</SelectItem>
                        <SelectItem value="FEMALE">Female</SelectItem>
                        <SelectItem value="OTHER">Other</SelectItem>
                          </SelectContent>
                        </Select>
                  </div>
                  </div>
                </div>

              <div className="rounded-xl border border-slate-200/70 bg-[#FAFBFC] p-4 shadow-sm ring-1 ring-slate-200/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Marketing</p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-600">Source *</Label>
              <select
                value={newLead.source}
                onChange={(e) => setNewLead((prev) => ({ ...prev, source: e.target.value }))}
                      className="mt-1.5 block h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm shadow-inner focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30"
              >
                <option value="">Select source</option>
                <option value="GOOGLE_PAID">Google Paid</option>
                      <option value="ORGANIC_DIRECT">Organic / direct (WhatsApp)</option>
                      <option value="INSTAGRAM_ORGANIC">Instagram organic</option>
                      <option value="ORGANIC_GOOGLE">Organic Google</option>
                      <option value="DIRECT_LANDING">Direct landing</option>
                <option value="GMB">Google My Business</option>
                <option value="META">Meta</option>
                <option value="PRACTO">Practo</option>
                <option value="HEXA">Hexa</option>
                <option value="REFERRAL">Referral</option>
                <option value="WALK_IN">Walk-in</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-600">Source details</Label>
              <Input
                value={newLead.sourceDetails}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, sourceDetails: e.target.value }))
                }
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                placeholder="Channel notes, ad group, referral doctor, etc."
              />
            </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-600">Campaign name</Label>
              <Input
                value={newLead.campaignName}
                onChange={(e) =>
                  setNewLead((prev) => ({ ...prev, campaignName: e.target.value }))
                }
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                      placeholder="Optional — ties to Meta / UTMs in ads"
              />
            </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200/70 bg-[#FAFBFC] p-4 shadow-sm ring-1 ring-slate-200/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Contact person</p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
                    <Label className="text-xs text-slate-600">Name</Label>
                    <Input
                      value={newLead.contactPersonName}
                      onChange={(e) =>
                        setNewLead((prev) => ({ ...prev, contactPersonName: e.target.value }))
                      }
                      placeholder="Who is calling / coordinating?"
                      className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
              />
            </div>
            <div>
                    <Label className="text-xs text-slate-600">Relationship</Label>
              <Select
                      value={newLead.contactRelationship.trim() ? newLead.contactRelationship : '__none__'}
                onValueChange={(v) =>
                  setNewLead((prev) => ({
                    ...prev,
                          contactRelationship: v === '__none__' ? '' : v,
                  }))
                }
              >
                      <SelectTrigger className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus:ring-2 focus:ring-indigo-500/30">
                        <SelectValue placeholder="Not specified" />
                </SelectTrigger>
                      <SelectContent className="max-h-56">
                        <SelectItem value="__none__">Not specified</SelectItem>
                        {createFormRelationshipOptions.map((r) => (
                          <SelectItem key={r} value={r}>
                            {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
              </div>
              </div>

              <div className="rounded-xl border border-slate-200/70 bg-[#FAFBFC] p-4 shadow-sm ring-1 ring-slate-200/40">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Procedure & dates
                </p>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
                    <Label className="text-xs text-slate-600">Is it a surgery case?</Label>
              <Select
                value={newLead.isSurgeryCase}
                onValueChange={(v) =>
                  setNewLead((prev) => ({
                    ...prev,
                          isSurgeryCase: v as NewLeadForm['isSurgeryCase'],
                          tentativeSurgeryDateLocal: v === 'yes' ? prev.tentativeSurgeryDateLocal : '',
                          aestheticProcedure: v === 'yes' ? prev.aestheticProcedure : '',
                  }))
                }
              >
                      <SelectTrigger className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus:ring-2 focus:ring-indigo-500/30">
                  <SelectValue placeholder="Not specified" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not specified</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
                  <div>
                    <Label className="text-xs text-slate-600">Procedure name</Label>
                    {newLead.isSurgeryCase === 'yes' ? (
                      <Select
                        value={
                          newLead.aestheticProcedure.trim()
                            ? newLead.aestheticProcedure
                            : '__none__'
                        }
                        onValueChange={(v) =>
                          setNewLead((prev) => ({
                            ...prev,
                            aestheticProcedure: v === '__none__' ? '' : v,
                          }))
                        }
                      >
                        <SelectTrigger className="mt-1.5 h-11 rounded-xl border-slate-200 bg-white shadow-inner focus:ring-2 focus:ring-indigo-500/30">
                          <SelectValue placeholder="Select procedure" />
                        </SelectTrigger>
                        <SelectContent className="max-h-52">
                          <SelectItem value="__none__">Not specified</SelectItem>
                          {createFormAestheticOptions.map((proc) => (
                            <SelectItem key={proc} value={proc}>
                              {proc}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="mt-3 text-xs text-slate-500">—</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-slate-600">Tentative surgery date</Label>
                    {newLead.isSurgeryCase === 'yes' ? (
                      <Input
                        type="date"
                        value={newLead.tentativeSurgeryDateLocal}
                        onChange={(e) =>
                          setNewLead((prev) => ({
                            ...prev,
                            tentativeSurgeryDateLocal: e.target.value,
                          }))
                        }
                        className="mt-1.5 h-11 max-w-full rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30 sm:max-w-xs"
                      />
                    ) : (
                      <p className="mt-2 text-xs text-slate-500">—</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Notes</Label>
                <Textarea
                  value={newLead.notes}
                  onChange={(e) => setNewLead((prev) => ({ ...prev, notes: e.target.value }))}
                  className="mt-2 min-h-[88px] rounded-xl border-slate-200 bg-white shadow-inner focus-visible:ring-2 focus-visible:ring-indigo-500/30"
                  placeholder="Anything the team should know…"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t border-slate-200/80 bg-slate-50/90 px-6 py-4 sm:gap-3">
              <Button
              type="button"
                variant="outline"
              className="rounded-xl border-slate-200"
                onClick={() => {
                  setCreateOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
              type="button"
              className="rounded-xl bg-indigo-600 px-5 text-white shadow-sm hover:bg-indigo-700"
              disabled={
                creating ||
                !newLead.patientName.trim() ||
                !newLead.phone.trim() ||
                !newLead.source
              }
                onClick={async () => {
                if (
                  !newLead.patientName.trim() ||
                  !newLead.phone.trim() ||
                  !newLead.source
                ) {
                    return;
                  }
                const rawAge = newLead.ageLocal.trim();
                const parsedAge =
                  rawAge === ''
                    ? undefined
                    : (() => {
                        const n = Number.parseInt(rawAge, 10);
                        return Number.isFinite(n) && n >= 0 ? n : undefined;
                      })();
                  setCreating(true);
                  try {
                    await leadsApi.createLead({
                      patientName: normalizePersonName(newLead.patientName),
                      phone: newLead.phone.trim(),
                    alternatePhone: newLead.alternatePhone.trim() || null,
                      email: newLead.email || undefined,
                    age: parsedAge,
                    gender: newLead.gender === 'unset' ? null : newLead.gender,
                      source: newLead.source,
                      sourceDetails: newLead.sourceDetails || undefined,
                      campaignName: newLead.campaignName.trim() || null,
                      notes: newLead.notes || undefined,
                    contactPersonName: normalizePersonNameOptional(newLead.contactPersonName),
                    contactRelationship: newLead.contactRelationship.trim() || null,
                    aestheticProcedureName:
                      newLead.isSurgeryCase === 'yes' && newLead.aestheticProcedure.trim()
                        ? newLead.aestheticProcedure.trim()
                        : null,
                      isSurgeryCase:
                        newLead.isSurgeryCase === 'unset'
                          ? null
                          : newLead.isSurgeryCase === 'yes',
                    tentativeSurgeryDate:
                      newLead.isSurgeryCase === 'yes' && newLead.tentativeSurgeryDateLocal
                        ? new Date(
                            `${newLead.tentativeSurgeryDateLocal}T00:00:00.000Z`
                          ).toISOString()
                        : null,
                  });
                  toast.success('Lead saved');
                    setCreateOpen(false);
                    setNewLead({
                      patientName: '',
                      phone: '',
                    alternatePhone: '',
                      email: '',
                    ageLocal: '',
                    gender: 'unset',
                      source: '',
                      sourceDetails: '',
                      campaignName: '',
                      notes: '',
                    contactPersonName: '',
                    contactRelationship: '',
                      isSurgeryCase: 'unset',
                    aestheticProcedure: '',
                    tentativeSurgeryDateLocal: '',
                    });
                    queryClient.invalidateQueries({ queryKey: ['leads'] });
                    queryClient.invalidateQueries({ queryKey: ['lead-filter-options'] });
                } catch (e: unknown) {
                  const ax = e as { response?: { data?: { message?: string } } };
                  toast.error(ax.response?.data?.message || 'Could not save lead');
                  } finally {
                    setCreating(false);
                  }
                }}
              >
              {creating ? 'Saving…' : 'Save lead'}
              </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={bulkOpen}
        onOpenChange={(open) => {
          setBulkOpen(open);
          if (!open) {
            setBulkFile(null);
            setBulkResult(null);
            if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk upload leads</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Download the CSV template, add one row per lead, then upload. Required:{' '}
              <strong>patientName</strong>, <strong>phone</strong>, <strong>source</strong>. Optional: email, age,
              gender, status, notes, owner email/name, follow-up dates (see template header).
            </p>
            {user?.role === 'SALES_PERSON' && !agencyViewOnly && (
              <p className="text-xs text-muted-foreground border-l-2 border-blue-200 pl-2">
                As a sales user, rows without <strong>assignedToEmail</strong> / <strong>assignedToName</strong> are
                assigned to you. Use those columns to route rows to another teammate when needed.
              </p>
            )}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={async () => {
                try {
                  const res = await leadsApi.downloadBulkImportTemplate();
                  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'leads-bulk-import-template.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch {
                  toast.error('Could not download template');
                }
              }}
            >
              Download template
            </Button>
            {user?.role === 'SUPER_ADMIN' && (
              <div className="space-y-1">
                <Label>Hospital ID (required)</Label>
                <Input
                  value={bulkHospitalId}
                  onChange={(e) => setBulkHospitalId(e.target.value)}
                  placeholder="Target hospital UUID"
                />
              </div>
            )}
            <div className="space-y-1">
              <Label>CSV file</Label>
              <input
                ref={bulkFileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setBulkFile(f ?? null);
                  setBulkResult(null);
                }}
              />
              <div className="flex flex-wrap gap-2 items-center">
                <Button type="button" variant="outline" size="sm" onClick={() => bulkFileInputRef.current?.click()}>
                  Choose file
                </Button>
                <span className="text-xs text-muted-foreground">{bulkFile?.name ?? 'No file selected'}</span>
              </div>
            </div>
            {bulkResult && (
              <div className="rounded-md border p-3 space-y-2 bg-muted/30">
                <p>
                  <strong>{bulkResult.created}</strong> created · <strong>{bulkResult.failed.length}</strong> failed ·{' '}
                  {bulkResult.rowCount} row(s)
                </p>
                {bulkResult.failed.length > 0 && (
                  <ul className="text-xs text-amber-800 max-h-32 overflow-y-auto list-disc pl-4">
                    {bulkResult.failed.map((f) => (
                      <li key={f.row}>
                        Row {f.row}: {f.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpen(false)}>
                Close
              </Button>
              <Button
                disabled={
                  !bulkFile ||
                  bulkUploading ||
                  (user?.role === 'SUPER_ADMIN' && !bulkHospitalId.trim())
                }
                onClick={async () => {
                  if (!bulkFile) return;
                  setBulkUploading(true);
                  setBulkResult(null);
                  try {
                    const res = await leadsApi.bulkImportLeads(
                      bulkFile,
                      user?.role === 'SUPER_ADMIN' ? bulkHospitalId : undefined
                    );
                    setBulkResult(res.data.data);
                    queryClient.invalidateQueries({ queryKey: ['leads'] });
                    toast.success(`Imported ${res.data.data.created} lead(s)`);
                  } catch (e: unknown) {
                    const msg =
                      (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                      'Bulk import failed';
                    toast.error(msg);
                  } finally {
                    setBulkUploading(false);
                  }
                }}
              >
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {autoShowTodayFollowUpPopup && (
      <Dialog
        open={followupPopupOpen}
        onOpenChange={(next) => {
          setFollowupPopupOpen(next);
          if (!next) todayFollowUpPopupDismissedRef.current = true;
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Today follow-up reminders ({todayFollowUps?.length ?? 0})</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {todayFollowUps && todayFollowUps.length > 0 ? (
              todayFollowUps.map((l) => (
                <div key={l.id} className="border rounded-md p-3 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="font-medium">{l.patientName}</div>
                    <div className="text-xs text-gray-600">
                      {l.phone} · {l.source} ·{' '}
                      {l.nextFollowUpAt
                        ? new Date(l.nextFollowUpAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                        : 'No date'}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    {user?.role === 'SALES_PERSON' && !agencyViewOnly && (
                      <>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={dismissFollowMut.isPending}
                          onClick={() => dismissFollowMut.mutate(l.id)}
                        >
                          Dismiss today
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={snoozeFollowMut.isPending}
                          onClick={() => snoozeFollowMut.mutate({ leadId: l.id, hours: 24 })}
                        >
                          Snooze 24h
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openLeadDetail(l)}>
                      Open lead
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-artis-slate">No follow-ups for today.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      )}

      <Dialog
        open={partnerDumpOpen}
        onOpenChange={(open) => {
          if (!open) setPartnerDumpOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Download lead dump</DialogTitle>
            <DialogDescription>
              Choose a created-date range. Phone numbers are not included in partner downloads.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="partner-dump-from">Created from</Label>
              <Input
                id="partner-dump-from"
                type="date"
                value={partnerDumpFrom}
                onChange={(e) => setPartnerDumpFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="partner-dump-to">Created to</Label>
              <Input
                id="partner-dump-to"
                type="date"
                value={partnerDumpTo}
                onChange={(e) => setPartnerDumpTo(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setPartnerDumpOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={partnerDumpDownloading || !partnerDumpFrom || !partnerDumpTo}
              onClick={async () => {
                if (!partnerDumpFrom || !partnerDumpTo) {
                  toast.error('Select both from and to dates');
                  return;
                }
                if (partnerDumpFrom > partnerDumpTo) {
                  toast.error('Created from must be on or before created to');
                  return;
                }
                setPartnerDumpDownloading(true);
                try {
                  const res = await leadsApi.downloadLeadDump({
                    from: partnerDumpFrom,
                    to: partnerDumpTo,
                  });
                  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute(
                    'download',
                    `leads-dump-${partnerDumpFrom}-to-${partnerDumpTo}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                  setPartnerDumpOpen(false);
                } catch (e: any) {
                  toast.error(e?.response?.data?.message || 'Failed to download lead dump');
                } finally {
                  setPartnerDumpDownloading(false);
                }
              }}
            >
              {partnerDumpDownloading ? 'Downloading…' : 'Download CSV'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

